import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import {
  AuditCase,
  AuditCaseDetail,
  AuditClosureCertificate,
  AuditCorrectiveAction,
  AuditCorrectiveActionAnalytics,
  AuditCorrectiveActionEscalationRule,
  AuditCorrectiveActionFilters,
  AuditCorrectiveActionLink,
  AuditCorrectiveActionReportRow,
  AuditDashboardSummary,
  AuditEvent,
  AuditEvidence,
  AuditFinding,
  AuditFindingAnalytics,
  AuditFindingEscalationRule,
  AuditFindingFilters,
  AuditFindingLink,
  AuditOverdueFindingReportRow,
  AuditProgram,
  AuditResponseBatch,
  AuditReviewRequest,
  AuditRootCauseTrend,
  AuditSourceAdapter,
  AuditSourceEvidenceImportRequest,
  AuditSourceResource,
  AuditSourceTimelineEvent,
  AuditTemplate,
  AuditTemplateVersion,
  AuditTransitionRequest
} from './audit.models';

@Injectable({ providedIn: 'root' })
export class AuditService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.restApiUrl}audit`;

  dashboardSummary(owningCompanyId?: number, companyGroupId?: number, memberCompanyId?: number): Observable<APIResponseWrapper<AuditDashboardSummary>> {
    const params = this.params({ owningCompanyId, companyGroupId, memberCompanyId });
    return this.http.get<APIResponseWrapper<AuditDashboardSummary>>(`${this.baseUrl}/dashboard/summary`, { params });
  }

  programs(owningCompanyId?: number): Observable<APIResponseWrapper<AuditProgram[]>> {
    return this.http.get<APIResponseWrapper<AuditProgram[]>>(`${this.baseUrl}/programs`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveProgram(program: AuditProgram): Observable<APIResponseWrapper<AuditProgram>> {
    if (program.auditProgramId) {
      return this.http.put<APIResponseWrapper<AuditProgram>>(
        `${this.baseUrl}/programs/${program.auditProgramId}`,
        program
      );
    }
    return this.http.post<APIResponseWrapper<AuditProgram>>(`${this.baseUrl}/programs`, program);
  }

  templates(owningCompanyId?: number): Observable<APIResponseWrapper<AuditTemplate[]>> {
    return this.http.get<APIResponseWrapper<AuditTemplate[]>>(`${this.baseUrl}/templates`, {
      params: this.params({ owningCompanyId })
    });
  }

  getTemplate(templateId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditTemplate>> {
    return this.http.get<APIResponseWrapper<AuditTemplate>>(`${this.baseUrl}/templates/${templateId}`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveTemplate(template: AuditTemplate): Observable<APIResponseWrapper<AuditTemplate>> {
    if (template.auditTemplateId) {
      return this.http.put<APIResponseWrapper<AuditTemplate>>(
        `${this.baseUrl}/templates/${template.auditTemplateId}`,
        template
      );
    }
    return this.http.post<APIResponseWrapper<AuditTemplate>>(`${this.baseUrl}/templates`, template);
  }

  replaceTemplateVersion(version: AuditTemplateVersion): Observable<APIResponseWrapper<AuditTemplateVersion>> {
    return this.http.put<APIResponseWrapper<AuditTemplateVersion>>(
      `${this.baseUrl}/template-versions/${version.templateVersionId}`,
      version
    );
  }

  publishTemplate(templateId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditTemplate>> {
    return this.http.post<APIResponseWrapper<AuditTemplate>>(
      `${this.baseUrl}/templates/${templateId}/publish`,
      null,
      { params: this.params({ owningCompanyId }) }
    );
  }

  cases(owningCompanyId?: number): Observable<APIResponseWrapper<AuditCase[]>> {
    return this.http.get<APIResponseWrapper<AuditCase[]>>(`${this.baseUrl}/cases`, {
      params: this.params({ owningCompanyId })
    });
  }

  createCase(auditCase: AuditCase): Observable<APIResponseWrapper<AuditCase>> {
    return this.http.post<APIResponseWrapper<AuditCase>>(`${this.baseUrl}/cases`, auditCase);
  }

  getCase(auditCaseId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditCaseDetail>> {
    return this.http.get<APIResponseWrapper<AuditCaseDetail>>(`${this.baseUrl}/cases/${auditCaseId}`, {
      params: this.params({ owningCompanyId })
    });
  }

  startCase(auditCaseId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditCase>> {
    return this.http.post<APIResponseWrapper<AuditCase>>(`${this.baseUrl}/cases/${auditCaseId}/start`, null, {
      params: this.params({ owningCompanyId })
    });
  }

  saveResponses(
    auditCaseId: number,
    request: AuditResponseBatch,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCaseDetail>> {
    return this.http.post<APIResponseWrapper<AuditCaseDetail>>(
      `${this.baseUrl}/cases/${auditCaseId}/responses`,
      request,
      { params: this.params({ owningCompanyId }) }
    );
  }

  submitCase(auditCaseId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditCase>> {
    return this.http.post<APIResponseWrapper<AuditCase>>(`${this.baseUrl}/cases/${auditCaseId}/submit`, null, {
      params: this.params({ owningCompanyId })
    });
  }

  reviewCase(
    auditCaseId: number,
    request: AuditReviewRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCase>> {
    return this.http.post<APIResponseWrapper<AuditCase>>(`${this.baseUrl}/cases/${auditCaseId}/review`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  closeCase(
    auditCaseId: number,
    request: AuditReviewRequest = {},
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCase>> {
    return this.http.post<APIResponseWrapper<AuditCase>>(`${this.baseUrl}/cases/${auditCaseId}/close`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  reopenCase(
    auditCaseId: number,
    request: AuditTransitionRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCase>> {
    return this.http.post<APIResponseWrapper<AuditCase>>(`${this.baseUrl}/cases/${auditCaseId}/reopen`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  sourceAdapters(owningCompanyId?: number): Observable<APIResponseWrapper<AuditSourceAdapter[]>> {
    return this.http.get<APIResponseWrapper<AuditSourceAdapter[]>>(`${this.baseUrl}/source-adapters`, {
      params: this.params({ owningCompanyId })
    });
  }

  sourceResources(
    sourceModule: string,
    sourceResourceType: string,
    query?: string,
    owningCompanyId?: number,
    limit = 20
  ): Observable<APIResponseWrapper<AuditSourceResource[]>> {
    return this.http.get<APIResponseWrapper<AuditSourceResource[]>>(`${this.baseUrl}/source-resources`, {
      params: this.params({ owningCompanyId, sourceModule, sourceResourceType, query, limit })
    });
  }

  sourcePreview(
    sourceModule: string,
    sourceResourceType: string,
    sourceResourceId: number,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditSourceResource>> {
    return this.http.get<APIResponseWrapper<AuditSourceResource>>(`${this.baseUrl}/source-resources/preview`, {
      params: this.params({ owningCompanyId, sourceModule, sourceResourceType, sourceResourceId })
    });
  }

  sourceTimeline(
    sourceModule: string,
    sourceResourceType: string,
    sourceResourceId: number,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditSourceTimelineEvent[]>> {
    return this.http.get<APIResponseWrapper<AuditSourceTimelineEvent[]>>(`${this.baseUrl}/source-resources/timeline`, {
      params: this.params({ owningCompanyId, sourceModule, sourceResourceType, sourceResourceId })
    });
  }

  importSourceEvidence(
    auditCaseId: number,
    request: AuditSourceEvidenceImportRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditEvidence>> {
    return this.http.post<APIResponseWrapper<AuditEvidence>>(
      `${this.baseUrl}/cases/${auditCaseId}/source-evidence`,
      request,
      { params: this.params({ owningCompanyId }) }
    );
  }

  findings(owningCompanyId?: number, auditCaseId?: number): Observable<APIResponseWrapper<AuditFinding[]>>;
  findings(filters?: AuditFindingFilters): Observable<APIResponseWrapper<AuditFinding[]>>;
  findings(
    owningCompanyIdOrFilters?: number | AuditFindingFilters,
    auditCaseId?: number
  ): Observable<APIResponseWrapper<AuditFinding[]>> {
    const params = typeof owningCompanyIdOrFilters === 'object'
      ? this.params(owningCompanyIdOrFilters as Record<string, string | number | boolean | null | undefined>)
      : this.params({ owningCompanyId: owningCompanyIdOrFilters, auditCaseId });
    return this.http.get<APIResponseWrapper<AuditFinding[]>>(`${this.baseUrl}/findings`, {
      params
    });
  }

  findingAnalytics(owningCompanyId?: number): Observable<APIResponseWrapper<AuditFindingAnalytics>> {
    return this.http.get<APIResponseWrapper<AuditFindingAnalytics>>(`${this.baseUrl}/findings/analytics`, {
      params: this.params({ owningCompanyId })
    });
  }

  createFinding(finding: AuditFinding): Observable<APIResponseWrapper<AuditFinding>> {
    return this.http.post<APIResponseWrapper<AuditFinding>>(`${this.baseUrl}/findings`, finding);
  }

  getFinding(findingId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditFinding>> {
    return this.http.get<APIResponseWrapper<AuditFinding>>(`${this.baseUrl}/findings/${findingId}`, {
      params: this.params({ owningCompanyId })
    });
  }

  updateFinding(finding: AuditFinding): Observable<APIResponseWrapper<AuditFinding>> {
    return this.http.put<APIResponseWrapper<AuditFinding>>(`${this.baseUrl}/findings/${finding.findingId}`, finding);
  }

  closeFinding(
    findingId: number,
    request: AuditTransitionRequest = {},
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditFinding>> {
    return this.http.post<APIResponseWrapper<AuditFinding>>(`${this.baseUrl}/findings/${findingId}/close`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  reopenFinding(
    findingId: number,
    request: AuditTransitionRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditFinding>> {
    return this.http.post<APIResponseWrapper<AuditFinding>>(`${this.baseUrl}/findings/${findingId}/reopen`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  escalateFinding(
    findingId: number,
    request: AuditTransitionRequest = {},
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditFinding>> {
    return this.http.post<APIResponseWrapper<AuditFinding>>(`${this.baseUrl}/findings/${findingId}/escalate`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  repeatCandidates(findingId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditFinding[]>> {
    return this.http.get<APIResponseWrapper<AuditFinding[]>>(`${this.baseUrl}/findings/${findingId}/repeat-candidates`, {
      params: this.params({ owningCompanyId })
    });
  }

  findingLinks(findingId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditFindingLink[]>> {
    return this.http.get<APIResponseWrapper<AuditFindingLink[]>>(`${this.baseUrl}/findings/${findingId}/related`, {
      params: this.params({ owningCompanyId })
    });
  }

  linkFinding(findingId: number, request: AuditFindingLink): Observable<APIResponseWrapper<AuditFindingLink>> {
    return this.http.post<APIResponseWrapper<AuditFindingLink>>(`${this.baseUrl}/findings/${findingId}/related`, request);
  }

  findingEscalationRules(owningCompanyId?: number): Observable<APIResponseWrapper<AuditFindingEscalationRule[]>> {
    return this.http.get<APIResponseWrapper<AuditFindingEscalationRule[]>>(`${this.baseUrl}/findings/escalation-rules`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveFindingEscalationRule(rule: AuditFindingEscalationRule): Observable<APIResponseWrapper<AuditFindingEscalationRule>> {
    if (rule.escalationRuleId) {
      return this.http.put<APIResponseWrapper<AuditFindingEscalationRule>>(
        `${this.baseUrl}/findings/escalation-rules/${rule.escalationRuleId}`,
        rule
      );
    }
    return this.http.post<APIResponseWrapper<AuditFindingEscalationRule>>(`${this.baseUrl}/findings/escalation-rules`, rule);
  }

  findingActions(findingId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditCorrectiveAction[]>> {
    return this.http.get<APIResponseWrapper<AuditCorrectiveAction[]>>(`${this.baseUrl}/findings/${findingId}/actions`, {
      params: this.params({ owningCompanyId })
    });
  }

  createFindingAction(
    findingId: number,
    action: AuditCorrectiveAction
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/findings/${findingId}/actions`, action);
  }

  actions(filters?: AuditCorrectiveActionFilters): Observable<APIResponseWrapper<AuditCorrectiveAction[]>>;
  actions(
    owningCompanyId?: number,
    findingId?: number,
    auditCaseId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction[]>>;
  actions(
    filtersOrOwningCompanyId?: AuditCorrectiveActionFilters | number,
    findingId?: number,
    auditCaseId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction[]>> {
    const filters = typeof filtersOrOwningCompanyId === 'object'
      ? filtersOrOwningCompanyId
      : { owningCompanyId: filtersOrOwningCompanyId, findingId, auditCaseId };
    return this.http.get<APIResponseWrapper<AuditCorrectiveAction[]>>(`${this.baseUrl}/actions`, {
      params: this.params(filters as Record<string, string | number | boolean | null | undefined>)
    });
  }

  actionAnalytics(owningCompanyId?: number): Observable<APIResponseWrapper<AuditCorrectiveActionAnalytics>> {
    return this.http.get<APIResponseWrapper<AuditCorrectiveActionAnalytics>>(`${this.baseUrl}/actions/analytics`, {
      params: this.params({ owningCompanyId })
    });
  }

  getAction(actionId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.get<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}`, {
      params: this.params({ owningCompanyId })
    });
  }

  actionLinks(actionId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditCorrectiveActionLink[]>> {
    return this.http.get<APIResponseWrapper<AuditCorrectiveActionLink[]>>(`${this.baseUrl}/actions/${actionId}/links`, {
      params: this.params({ owningCompanyId })
    });
  }

  linkAction(actionId: number, link: AuditCorrectiveActionLink): Observable<APIResponseWrapper<AuditCorrectiveActionLink>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveActionLink>>(`${this.baseUrl}/actions/${actionId}/links`, link);
  }

  actionClosureCertificate(
    actionId: number,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditClosureCertificate>> {
    return this.http.get<APIResponseWrapper<AuditClosureCertificate>>(
      `${this.baseUrl}/actions/${actionId}/closure-certificate`,
      { params: this.params({ owningCompanyId }) }
    );
  }

  actionEscalationRules(owningCompanyId?: number): Observable<APIResponseWrapper<AuditCorrectiveActionEscalationRule[]>> {
    return this.http.get<APIResponseWrapper<AuditCorrectiveActionEscalationRule[]>>(
      `${this.baseUrl}/actions/escalation-rules`,
      { params: this.params({ owningCompanyId }) }
    );
  }

  saveActionEscalationRule(
    rule: AuditCorrectiveActionEscalationRule
  ): Observable<APIResponseWrapper<AuditCorrectiveActionEscalationRule>> {
    if (rule.actionEscalationRuleId) {
      return this.http.put<APIResponseWrapper<AuditCorrectiveActionEscalationRule>>(
        `${this.baseUrl}/actions/escalation-rules/${rule.actionEscalationRuleId}`,
        rule
      );
    }
    return this.http.post<APIResponseWrapper<AuditCorrectiveActionEscalationRule>>(
      `${this.baseUrl}/actions/escalation-rules`,
      rule
    );
  }

  updateAction(action: AuditCorrectiveAction): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.put<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${action.actionId}`, action);
  }

  submitAction(
    actionId: number,
    request: AuditTransitionRequest = {},
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}/submit`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  verifyAction(
    actionId: number,
    request: AuditTransitionRequest = {},
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}/verify`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  rejectAction(
    actionId: number,
    request: AuditTransitionRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}/reject`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  closeAction(
    actionId: number,
    request: AuditTransitionRequest = {},
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}/close`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  reopenAction(
    actionId: number,
    request: AuditTransitionRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}/reopen`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  escalateAction(
    actionId: number,
    request: AuditTransitionRequest,
    owningCompanyId?: number
  ): Observable<APIResponseWrapper<AuditCorrectiveAction>> {
    return this.http.post<APIResponseWrapper<AuditCorrectiveAction>>(`${this.baseUrl}/actions/${actionId}/escalate`, request, {
      params: this.params({ owningCompanyId })
    });
  }

  evidence(
    owningCompanyId?: number,
    auditCaseId?: number,
    findingId?: number,
    actionId?: number
  ): Observable<APIResponseWrapper<AuditEvidence[]>> {
    return this.http.get<APIResponseWrapper<AuditEvidence[]>>(`${this.baseUrl}/evidence`, {
      params: this.params({ owningCompanyId, auditCaseId, findingId, actionId })
    });
  }

  createEvidence(evidence: AuditEvidence): Observable<APIResponseWrapper<AuditEvidence>> {
    return this.http.post<APIResponseWrapper<AuditEvidence>>(`${this.baseUrl}/evidence`, evidence);
  }

  caseEvents(auditCaseId: number, owningCompanyId?: number): Observable<APIResponseWrapper<AuditEvent[]>> {
    return this.http.get<APIResponseWrapper<AuditEvent[]>>(`${this.baseUrl}/cases/${auditCaseId}/events`, {
      params: this.params({ owningCompanyId })
    });
  }

  correctiveActionReport(owningCompanyId?: number): Observable<APIResponseWrapper<AuditCorrectiveActionReportRow[]>> {
    return this.http.get<APIResponseWrapper<AuditCorrectiveActionReportRow[]>>(
      `${this.baseUrl}/reports/corrective-actions`,
      { params: this.params({ owningCompanyId }) }
    );
  }

  rootCauseTrends(owningCompanyId?: number): Observable<APIResponseWrapper<AuditRootCauseTrend[]>> {
    return this.http.get<APIResponseWrapper<AuditRootCauseTrend[]>>(
      `${this.baseUrl}/reports/root-cause-trends`,
      { params: this.params({ owningCompanyId }) }
    );
  }

  overdueFindingsReport(owningCompanyId?: number): Observable<APIResponseWrapper<AuditOverdueFindingReportRow[]>> {
    return this.http.get<APIResponseWrapper<AuditOverdueFindingReportRow[]>>(
      `${this.baseUrl}/reports/overdue-findings`,
      { params: this.params({ owningCompanyId }) }
    );
  }

  private params(values: Record<string, string | number | boolean | null | undefined>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(values)) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return params;
  }
}
